import React, { useState } from "react";
import { useLatestBySymbol } from "../hooks/useWebSocket";
import { useApi } from "../hooks/useApi";

// v5.16: the asset universe and its metadata come from /intelligence.asset_meta,
// which the backend derives from config. There is deliberately NO hardcoded list
// here any more.
//
// ⚠ There used to be one, and it went stale exactly as you would expect: the
// v5.14 swap replaced USDJPY/USDCHF/USDCAD with US500/USTEC/XAUUSD in the engine,
// this file did not move, and the dashboard kept showing three assets that no
// longer existed while hiding the three that did. Do not reintroduce a literal
// roster — change config and every panel follows on the next poll.

// Fallback ONLY for the first render before /intelligence resolves. Empty, not a
// guessed list: showing nothing briefly is honest, showing the wrong assets is not.
const EMPTY_META = {};

function dp(sym, meta) {
  // Decimals are per instrument: 5 for FX, 3 for JPY crosses, 2 for indices and
  // metals. Reading it from config via asset_meta means a new asset class cannot
  // be rendered at the wrong precision.
  const d = meta?.[sym]?.decimals;
  return typeof d === "number" ? d : 5;
}

function sigStyle(a) {
  return {
    fontSize: "11px", fontWeight: 700, padding: "2px 7px", borderRadius: "4px", display: "inline-block",
    background: a === "BUY" ? "#166534" : a === "SELL" ? "#7f1d1d" : "#1e2a45",
    color:      a === "BUY" ? "#4ade80" : a === "SELL" ? "#f87171" : "#64748b",
  };
}

function ConfBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 65 ? "#22c55e" : pct >= 55 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ marginTop: "4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "9px", color: "#475569", marginBottom: "2px" }}>
        <span>AI Confidence</span><span style={{ color }}>{pct}%</span>
      </div>
      <div style={{ height: "3px", background: "#1e2a45", borderRadius: "2px" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: "2px", transition: "width 0.5s" }} />
      </div>
    </div>
  );
}

// Check icon for pre-trade validation
function CheckDot({ pass }) {
  return (
    <span style={{
      display: "inline-block", width: "6px", height: "6px", borderRadius: "50%",
      background: pass ? "#22c55e" : "#ef4444", flexShrink: 0,
    }} />
  );
}

export default function AssetGrid() {
  const { bySymbol: ticks } = useLatestBySymbol("ticks");
  const { data: intel }     = useApi("/intelligence", 30000);
  // v5.16: roster + metadata straight from the backend's config. Sorted so the
  // grid order is stable between polls rather than following dict order.
  const metaMap   = intel?.asset_meta || EMPTY_META;
  const assetMeta = Object.keys(metaMap).sort();
  const { data: posData }   = useApi("/positions", 5000);
  const [expanded, setExpanded] = useState(null);

  const positions = posData?.positions || [];
  const symIntel  = Object.fromEntries((intel?.symbols || []).map(s => [s.symbol, s]));
  // v5.24: the ENGINE's own last entry decision per symbol. The per-symbol "gates" /
  // "entry_blocked" / "pretrade" fields are a partial re-computation (the sentiment gate
  // was hardcoded to pass) — they drove a "✓ confirmed" badge while the engine blocked the
  // trade. Only this verdict may say a trade happened or why it did not.
  const verdicts   = intel?.engine_verdicts || {};
  const staleAfter = intel?.verdict_stale_after_sec ?? 1200;
  // v5.25a (2026-09-17): the per-asset Settings switch. Only an explicit false shows the
  // label, so an older API without the field shows nothing rather than "off".
  const tradingEnabled = intel?.trading_enabled || {};
  // "error" = the switch was never read (DB down at start): OFF is fail-closed, not a choice.
  const tradingSource  = intel?.trading_switch_source || {};

  return (
    <div style={{ background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "14px" }}>
      <div style={{ fontWeight: 600, color: "#94a3b8", marginBottom: "10px", fontSize: "13px" }}>📈 Asset Intelligence</div>
      <div className="asset-grid">
        {assetMeta.length === 0 ? (
          <div style={{ color: "#475569", fontSize: "12px", padding: "8px" }}>
            Loading asset universe…
          </div>
        ) : null}
        {assetMeta.map((sym) => {
          const tick  = ticks[sym]  || {};
          const info  = symIntel[sym] || {};
          const pos   = positions.find(p => p.symbol === sym);
          const d     = dp(sym, metaMap);
          const meta  = metaMap[sym] || {};

          const mid    = tick.mid || tick.bid || info.price || 0;
          const action = info.ai_action || "—";
          const conf   = info.ai_confidence || 0;
          const blocked = info.entry_blocked;
          const isMarketClosed = info.market_closed === true;
          const allGatesPass = !blocked && action !== "HOLD" && action !== "—";
          const isExpanded = expanded === sym;
          const pt = info.pretrade;
          const gatesPassButNotApproved = allGatesPass && pt && !pt.approved;
          const fullyApproved = allGatesPass && pt && pt.approved;
          const ev = verdicts[sym];
          const evFresh = !!ev && ev.age_sec != null && ev.age_sec <= staleAfter;
          const engineEntered = evFresh && ev.outcome === "ENTERED";
          const engineStopped = evFresh && ["BLOCKED", "ORDER_REJECTED", "PARTIAL_FILL"].includes(ev.outcome);

          return (
            <div key={sym} style={{
              background: pos ? (pos.type === "BUY" ? "#052e16" : "#450a0a") : "#080c18",
              border: `1px solid ${pos ? (pos.type === "BUY" ? "#166534" : "#7f1d1d") : engineEntered ? "#14532d" : engineStopped ? "#78350f" : "#1e2a45"}`,
              borderRadius: "8px", padding: "10px",
            }}>
              {/* Symbol + price */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "2px" }}>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#94a3b8" }}>{sym}</span>
                <span style={{ fontSize: "14px", fontWeight: 700, color: "#e2e8f0" }}>
                  {mid > 0 ? mid.toFixed(d) : "—"}
                </span>
              </div>
              {/* Asset type + session */}
              <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
                <span style={{ fontSize: "8px", color: "#475569", background: "#111827", borderRadius: "3px", padding: "1px 4px" }}>
                  {meta.type}
                </span>
                <span style={{ fontSize: "8px", color: "#334155", background: "#0f172a", borderRadius: "3px", padding: "1px 4px" }}>
                  {meta.session}
                </span>
                <span style={{ fontSize: "8px", color: meta.tier === 1 ? "#a78bfa" : "#60a5fa", background: "#0f172a", borderRadius: "3px", padding: "1px 4px" }}>
                  T{meta.tier}
                </span>
                {tradingEnabled[sym] === false && tradingSource[sym] === "error" && (
                  <span style={{ fontSize: "8px", color: "#f87171", background: "#450a0a", borderRadius: "3px", padding: "1px 4px" }}>
                    trading switch unreadable — blocked (fail-closed)
                  </span>
                )}
                {tradingEnabled[sym] === false && tradingSource[sym] !== "error" && (
                  <span style={{ fontSize: "8px", color: "#f59e0b", background: "#78350f33", borderRadius: "3px", padding: "1px 4px" }}>
                    trading off — learning only
                  </span>
                )}
              </div>

              {/* AI signal + entry status */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                <span style={sigStyle(action)}>{action}</span>
                {isMarketClosed && (
                  <span style={{ fontSize: "9px", color: "#f59e0b", background: "#78350f33", padding: "1px 5px", borderRadius: "3px" }}>Market closed</span>
                )}
                {evFresh && ev.outcome === "ENTERED" && (
                  <span style={{ fontSize: "9px", color: "#22c55e" }}>✓ entered</span>
                )}
                {evFresh && ev.outcome === "PARTIAL_FILL" && (
                  <span style={{ fontSize: "9px", color: "#f59e0b" }}>◐ {ev.stage_label}</span>
                )}
                {evFresh && ev.outcome === "ORDER_REJECTED" && (
                  <span style={{ fontSize: "9px", color: "#f87171" }}>✗ broker: {ev.message}</span>
                )}
                {evFresh && ev.outcome === "BLOCKED" && (
                  <span style={{ fontSize: "9px", color: "#f59e0b" }} title={ev.message || ""}>
                    ⛔ {ev.stage_label}
                  </span>
                )}
                {evFresh && ["ERROR", "UNATTRIBUTED", "UNKNOWN", "SUBMITTED"].includes(ev.outcome) && (
                  <span style={{ fontSize: "9px", color: "#94a3b8" }}>? {ev.stage_label}</span>
                )}
                {!evFresh && !pos && action !== "HOLD" && action !== "—" && (
                  <span style={{ fontSize: "9px", color: "#64748b" }}>⏳ not yet evaluated by the engine</span>
                )}
              </div>

              {/* Pre-Trade Validation — shown when gates pass but confirmations fail */}
              {gatesPassButNotApproved && pt && (
                <div style={{
                  marginBottom: "4px", padding: "5px 6px",
                  background: "#1a1400", borderRadius: "4px",
                  border: "1px solid #78350f",
                }}>
                  <div style={{ fontSize: "9px", color: "#fde68a", marginBottom: "3px", fontWeight: 600 }}>
                    Pre-Trade Checks: {pt.passed}/{pt.required} needed ({pt.zone === "soft" ? "55-58% zone" : "58%+ zone"})
                  </div>
                  {pt.checks.map((c, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: "5px",
                      fontSize: "8px", padding: "1px 0",
                      color: c.pass ? "#4ade80" : "#fca5a5",
                    }}>
                      <CheckDot pass={c.pass} />
                      <span style={{ color: "#94a3b8", width: "80px" }}>{c.name}</span>
                      <span>{c.detail}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Pre-Trade Validation — compact when fully approved */}
              {fullyApproved && pt && (
                <div style={{ fontSize: "8px", color: "#4ade80", marginBottom: "3px",
                              display: "flex", alignItems: "center", gap: "4px" }}>
                  <span>pre-trade checks {pt.passed}/{pt.checks?.length}</span>
                  {pt.checks?.filter(c => !c.pass).map((c, i) => (
                    <span key={i} style={{ color: "#f59e0b" }}>({c.name})</span>
                  ))}
                </div>
              )}

              {/* Correlation block reason */}
              {info.gates?.correlation && !info.gates.correlation.pass && (
                <div style={{ fontSize: "9px", color: "#fca5a5", marginBottom: "3px",
                              padding: "2px 4px", background: "#1a050522", borderRadius: "3px",
                              borderLeft: "2px solid #ef4444" }}>
                  🔗 {info.gates.correlation.reason
                    ?.replace(/CORR_SAME_(\w+)_(\w+)_(\w+)/, "Same bet as $2")
                    .replace(/CORR_OPPOSITE_(\w+)_(\w+)/, "Contradicts $2")}
                </div>
              )}

              {/* Confidence bar / no-signal status — a HOLD asset (conf 0) is not
                  "empty": explain WHY it has no directional confidence so the tile
                  still shows data rather than rendering blank next to EURUSD. */}
              {isMarketClosed ? (
                <div style={{ marginTop: "4px", fontSize: "9px", color: "#f59e0b" }}>
                  AI signal dampened (market closed)
                </div>
              ) : conf > 0 ? (
                <ConfBar value={conf} />
              ) : (
                <div style={{ marginTop: "4px", fontSize: "9px", color: "#475569", lineHeight: "1.4" }}>
                  {meta.type === "Range Cross"
                    ? "Mean-reversion — awaiting band extreme"
                    : "No directional edge — model rejected (quality gate)"}
                </div>
              )}

              {/* Open position P&L */}
              {pos && (
                <div style={{ marginTop: "5px", paddingTop: "5px", borderTop: "1px solid #1e2a45" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                    <span style={{ color: "#64748b" }}>{pos.volume}L {pos.comment?.replace("Sentinel_","[")}{pos.comment ? "]" : ""}</span>
                    <span style={{ color: pos.profit >= 0 ? "#4ade80" : "#f87171", fontWeight: 700 }}>
                      {pos.profit >= 0 ? "+" : ""}${pos.profit?.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Spread */}
              {tick.spread > 0 && (
                <div style={{ fontSize: "9px", color: "#334155", marginTop: "3px" }}>
                  Spread: {tick.spread.toFixed(d)}
                </div>
              )}

              {/* Unfoldable AI Reasoning */}
              <div
                onClick={() => setExpanded(isExpanded ? null : sym)}
                style={{
                  fontSize: "9px", color: "#475569", marginTop: "4px",
                  cursor: "pointer", userSelect: "none",
                  display: "flex", alignItems: "center", gap: "4px",
                }}
              >
                <span style={{ fontSize: "8px", transition: "transform 0.2s",
                               transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                <span>AI Reasoning</span>
              </div>

              {isExpanded && info.ai_reasoning && (
                <div style={{
                  marginTop: "4px", padding: "6px 8px",
                  background: "#0a0e1a", borderRadius: "4px",
                  border: "1px solid #1e2a45",
                  fontSize: "9px", color: "#94a3b8", lineHeight: "1.5",
                }}>
                  {info.ai_reasoning}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
